<script setup lang="ts">
import { ref, useTemplateRef } from "vue";

import MenuItemDelimiter from "@/components/menu/MenuItemDelimiter.vue";
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import DropdownMenu from "@/components/ui/DropdownMenu/DropdownMenu.vue";
import SelectableList from "@/components/ui/SelectableList/SelectableList.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import type { Icon } from "@/model/icon";

interface BreadCrumbItem {
  id: string;
  name: string;
  icon?: Icon;
  mark?: Icon;
}

defineProps<{
  icon?: Icon;
  first?: boolean;
  tooltip: string;
  title: string;
  items: BreadCrumbItem[];
  value: BreadCrumbItem;
}>();
const emit = defineEmits<{
  change: [item: BreadCrumbItem];
  active: []; // selector is hovered or dropdown is open
  inactive: [];
}>();

defineExpose({ open: () => void dropdownRef.value?.open() });

const dropdownRef = useTemplateRef("dropdown");
const open = ref(false);

function hoverActive() {
  emit("active");
}

function openActive() {
  emit("active");
  open.value = true;
}

function hoverInactive() {
  if (!open.value) {
    emit("inactive");
  }
}

function closeInactive() {
  emit("inactive");
  open.value = false;
}
</script>

<template>
  <span class="bread-crumb-selector" @pointerenter="hoverActive" @pointerleave="hoverInactive">
    <MenuItemDelimiter v-if="!first" />
    <DropdownMenu
      ref="dropdown"
      style="max-height: 300px"
      :offset="{ top: 12 }"
      width="md"
      @open="openActive"
      @close="closeInactive"
    >
      <template #trigger="{ isOpen }">
        <BaseTooltip class="tooltip">
          <template #content>
            {{ tooltip }}
          </template>
          <BaseButton
            variant="ghost"
            color="grey"
            data-dropdown-trigger
            :icon-before="icon"
            :activated="isOpen"
            :aria-expanded="isOpen"
            aria-controls="bread-crumb-selector"
            aria-haspopup="menu"
          >
            <span class="text">{{ value.name }}</span>
          </BaseButton>
        </BaseTooltip>
      </template>
      <SelectableList
        id="bread-crumb-selector"
        v-keyboard-nav
        role="dialog"
        :header="title"
        :values="items"
        :model-value="value"
        @update:model-value="emit('change', $event)"
      >
        <template #default="{ item }">
          {{ item.name }}
        </template>
        <template v-if="value.icon" #before="{ item }">
          <SvgIcon :name="item.icon!" />
        </template>
        <template #after="{ item }">
          <SvgIcon v-if="item.mark" :name="item.mark" />
        </template>
      </SelectableList>
    </DropdownMenu>
  </span>
</template>

<style lang="scss">
@use "@/styles/font";

.bread-crumb-selector {
  display: flex;
  align-items: center;
  overflow: hidden;

  .dropdown-menu {
    overflow: hidden;
  }

  .base-button {
    overflow: hidden;
    font-weight: font.$weight_normal;
  }

  .tooltip {
    overflow: hidden;
    display: flex;
  }

  & > svg {
    flex-shrink: 0;
  }
}
</style>
